import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { ObservadorDeAlvo } from '../tour-mode/tour-alvo-visivel';
import { AlvoRealce, ScreenContextService } from '../screen-context.service';
import { TourRoteiroService } from '../tour-mode/tour-roteiro.service';
import { AiAgentService } from '../API/ai-agent.service';

export const AI_REALCE_MAX_ALVOS = 5;
export const AI_REALCE_DURACAO_MS = 6000;

/**
 * Faróis do ASSISTENTE (spec §6.1). Reutiliza `ObservadorDeAlvo` do tour (uma instância por alvo),
 * mas nunca toca no `TourService`: canal próprio, cor própria, prioridade do tour por construção —
 * `acender` é no-op com tour ativo e qualquer tour que comece apaga tudo.
 */
@Injectable({ providedIn: 'root' })
export class AiRealceService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly screenContext = inject(ScreenContextService);
  private readonly tourRoteiro = inject(TourRoteiroService);
  private readonly ai = inject(AiAgentService);
  private readonly router = inject(Router);

  private readonly ehNavegador = isPlatformBrowser(this.platformId);

  private observadores: ObservadorDeAlvo[] = [];
  private assinaturas: Subscription[] = [];
  private rects: (DOMRect | null)[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;

  private readonly _retangulos = signal<readonly DOMRect[]>([]);
  readonly retangulos = this._retangulos.asReadonly();

  constructor() {
    if (!this.ehNavegador) return;
    effect(() => { if (!this.ai.aberto()) this.apagar(); });
    effect(() => { if (this.tourRoteiro.ativo()) this.apagar(); });
    this.router.events
      .pipe(filter((e) => e instanceof NavigationStart), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.apagar());
  }

  /** Acende até {@link AI_REALCE_MAX_ALVOS} alvos; devolve quantos resolveram. Substitui o conjunto anterior. */
  acender(alvos: AlvoRealce[]): number {
    if (!this.ehNavegador || this.tourRoteiro.ativo()) return 0;
    this.apagar();
    const vistos = new Set<HTMLElement>();
    for (const alvo of alvos) {
      if (vistos.size >= AI_REALCE_MAX_ALVOS) break;
      const el = this.screenContext.ancora(alvo);
      if (el && !vistos.has(el)) vistos.add(el);
    }
    const win = this.doc.defaultView;
    Array.from(vistos).forEach((el, i) => {
      const obs = new ObservadorDeAlvo(this.doc, win);
      this.observadores.push(obs);
      this.rects.push(null);
      this.assinaturas.push(obs.retangulo$.subscribe((r) => {
        this.rects[i] = r;
        this.publicar();
      }));
      obs.observar(el);
    });
    if (vistos.size > 0) this.timer = setTimeout(() => this.apagar(), AI_REALCE_DURACAO_MS);
    return vistos.size;
  }

  apagar(): void {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    this.assinaturas.forEach((s) => s.unsubscribe());
    this.observadores.forEach((o) => o.destruir());
    this.assinaturas = [];
    this.observadores = [];
    this.rects = [];
    if (this._retangulos().length > 0) this._retangulos.set([]);
  }

  /** Remede todos os alvos agora (gancho de teste e de mudança de layout). */
  remedir(): void {
    this.observadores.forEach((o) => o.medir());
  }

  private publicar(): void {
    this._retangulos.set(this.rects.filter((r): r is DOMRect => r !== null));
  }
}
